// src/bookmark.js
import { getMap } from './mapInstance.js';
import * as olProj from 'ol/proj';
import { API_URL } from './config.js';
import { getSelectedCountryCode, getSelectedLanguageCode } from './country-language.js';
import { makeDraggable } from './mapUtils.js';
import { registerDock, setDockVisible } from './panelDockManager.js';
import { placeFloatingDialog } from './dialogs.js';

let bookmarkMinimized = false;
let bookmarkStoredGeometry = null;
let reloadBookmarks = async () => {};

export function loadBookmarks() {
  return reloadBookmarks();
}

export function setupBookmarkForm() {

  const token = sessionStorage.getItem('access_token');

  // --- Create panel ---
  const panel = document.createElement('div');
  panel.id = 'bookmark-panel';
  panel.className = 'p-3 border rounded bg-light bookmark-panel-shell';
  panel.style.width = '350px'; // bigger form
  panel.style.position = 'absolute';
  panel.style.top = '100px';
  panel.style.right = '20px';
  panel.style.zIndex = '2000';
  panel.style.cursor = 'move';
  panel.style.display = 'none'; // hidden initially
  panel.style.opacity = '1';

  // --- Panel Header ---
  const header = document.createElement('div');
  header.className = 'bookmark-panel-header d-flex justify-content-between align-items-center mb-2';
  header.style.cursor = 'move';

  const title = document.createElement('h6');
  title.textContent = 'Bookmarks';
  title.style.margin = 0;

  const opacityControl = document.createElement('label');
  opacityControl.className = 'summary-opacity-control';
  opacityControl.title = 'Transparency';
  opacityControl.setAttribute('data-no-drag', 'true');
  opacityControl.innerHTML = `
    <i class="bi bi-circle-half" aria-hidden="true"></i>
    <span class="summary-opacity-slider">
      <input id="bookmark-opacity" type="range" min="30" max="100" step="5" value="100" aria-label="Bookmarks transparency">
      <output for="bookmark-opacity" style="left:47px">100%</output>
    </span>`;

  let bookmarkOpacityLabelTimer = null;
  opacityControl.querySelector('#bookmark-opacity')?.addEventListener('input', event => {
    const value = Number(event.currentTarget.value);
    panel.style.opacity = String(value / 100);
    const output = opacityControl.querySelector('output[for="bookmark-opacity"]');
    if (!output) return;

    output.textContent = `${value}%`;
    output.style.left = `${7 + ((value - 30) / 70) * 40}px`;
    output.classList.add('is-visible');
    window.clearTimeout(bookmarkOpacityLabelTimer);
    bookmarkOpacityLabelTimer = window.setTimeout(
      () => output.classList.remove('is-visible'),
      2000
    );
  });

  // Right-side buttons container
  const headerBtns = document.createElement('div');
  headerBtns.className = 'd-flex align-items-center gap-1';

  const minimizeBtn = document.createElement('button');
  minimizeBtn.type = 'button';
  minimizeBtn.className = 'btn btn-sm btn-outline-secondary sevenn-panel-toggle bookmark-panel-minimize-btn';
  minimizeBtn.innerHTML = '<i class="bi bi-dash-lg"></i>';
  minimizeBtn.title = 'Dock to left';
  minimizeBtn.setAttribute('aria-label', 'Dock bookmark panel to left');
  minimizeBtn.setAttribute('data-no-drag', 'true');

  // Close button (Bootstrap style)
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'btn-close btn-sm';
  closeBtn.title = 'Close';
  closeBtn.setAttribute('data-no-drag', 'true');

  // Close event
  closeBtn.addEventListener('click', () => {
    panel.style.display = 'none';
    bookmarkMinimized = false;
    setDockVisible('bookmark', false);
    loadBookmarksInternal();
  });

  headerBtns.appendChild(minimizeBtn);
  headerBtns.appendChild(closeBtn);

  header.appendChild(title);
  header.appendChild(opacityControl);
  header.appendChild(headerBtns);
  panel.appendChild(header);

  // --- Collapsible content container ---
  const content = document.createElement('div');
  content.id = 'bookmark-content';
  content.className = 'bookmark-panel-body';
  panel.appendChild(content);

  // --- Form inside content ---
  const form = document.createElement('form');
  form.id = 'bookmarkForm';

  // Create row for label, input, and button
  const rowDiv = document.createElement('div');
  rowDiv.className = 'row g-2 align-items-center';

  // Label
  const labelDiv = document.createElement('div');
  labelDiv.className = 'col-auto';
  const label = document.createElement('label');
  label.htmlFor = 'bookmarkName';
  label.className = 'col-form-label';
  label.textContent = 'Bookmark:';
  labelDiv.appendChild(label);
  rowDiv.appendChild(labelDiv);

  // Input
  const inputDiv = document.createElement('div');
  inputDiv.className = 'col';
  const input = document.createElement('input');
  input.type = 'text';
  input.id = 'bookmarkName';
  input.className = 'form-control form-control-sm';
  input.placeholder = 'Enter name';
  input.required = true;
  inputDiv.appendChild(input);
  rowDiv.appendChild(inputDiv);

  // Save button
  const buttonDiv = document.createElement('div');
  buttonDiv.className = 'col-auto';
  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'btn btn-primary btn-sm';
  submitBtn.textContent = 'Save';
  buttonDiv.appendChild(submitBtn);
  rowDiv.appendChild(buttonDiv);

  form.appendChild(rowDiv);

  // Message box
  const messageBox = document.createElement('div');
  messageBox.id = 'bookmarkMessage';
  messageBox.className = 'mt-2 text-success';
  messageBox.style.display = 'none';
  form.appendChild(messageBox);

  content.appendChild(form);

  // --- Bookmark List ---
  const listTitle = document.createElement('h6');
  listTitle.textContent = 'Saved Bookmarks';
  listTitle.className = 'mt-3';
  content.appendChild(listTitle);

  const bookmarkList = document.createElement('ul');
  bookmarkList.id = 'bookmarkList';
  bookmarkList.className = 'list-group list-group-flush';
  bookmarkList.style.maxHeight = '200px'; // approx. 6 items (adjust if needed)
  bookmarkList.style.overflowY = 'auto';
  bookmarkList.style.paddingRight = '4px'; // avoid hiding scrollbar under border
  content.appendChild(bookmarkList);

  document.body.appendChild(panel);

  const dockButton = document.createElement('button');
  dockButton.type = 'button';
  dockButton.id = 'bookmark-panel-dock';
  dockButton.className = 'bookmark-panel-dock btn btn-light border border-start-0 shadow-sm';
  dockButton.innerHTML = '<i class="bi bi-chevron-left"></i>';
  dockButton.title = 'Restore bookmarks';
  dockButton.setAttribute('aria-label', 'Restore bookmark panel');
  dockButton.style.display = 'none';
  document.body.appendChild(dockButton);
  registerDock('bookmark', dockButton, 100, '.bookmark-button');

  const capturePanelGeometry = () => ({
    left: panel.style.left || '',
    right: panel.style.right || '',
    top: panel.style.top || '',
    bottom: panel.style.bottom || '',
    width: panel.style.width || '',
    height: panel.style.height || '',
  });

  const applyPanelGeometry = (geometry) => {
    if (!geometry) return;
    if (geometry.left) panel.style.setProperty('left', geometry.left, 'important');
    else panel.style.removeProperty('left');
    if (geometry.right) panel.style.setProperty('right', geometry.right, 'important');
    else panel.style.removeProperty('right');
    if (geometry.top) panel.style.setProperty('top', geometry.top, 'important');
    else panel.style.removeProperty('top');
    if (geometry.bottom) panel.style.setProperty('bottom', geometry.bottom, 'important');
    else panel.style.removeProperty('bottom');
    if (geometry.width) panel.style.setProperty('width', geometry.width, 'important');
    else panel.style.removeProperty('width');
    if (geometry.height) panel.style.setProperty('height', geometry.height, 'important');
    else panel.style.removeProperty('height');
  };

  const applyBookmarkPanelState = () => {
    if (!panel || !content || !header || !dockButton) return;

    panel.classList.toggle('is-minimized', bookmarkMinimized);
    panel.style.display = bookmarkMinimized ? 'none' : 'block';
    if (!bookmarkMinimized && bookmarkStoredGeometry) {
      applyPanelGeometry(bookmarkStoredGeometry);
    }
    if (!bookmarkMinimized && !bookmarkStoredGeometry) {
      panel.style.width = '350px';
      panel.style.height = '';
      panel.style.right = '20px';
      panel.style.top = '100px';
    }
    panel.style.padding = '1rem';
    panel.style.cursor = 'move';
    panel.style.overflow = 'visible';

    header.style.display = '';
    content.style.display = '';
    setDockVisible('bookmark', bookmarkMinimized);
  };

  const setBookmarkPanelMinimized = (minimized) => {
    if (minimized) {
      bookmarkStoredGeometry = capturePanelGeometry();
    }
    bookmarkMinimized = minimized;
    applyBookmarkPanelState();
    if (!bookmarkMinimized) loadBookmarksInternal();
  };

  minimizeBtn.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    setBookmarkPanelMinimized(true);
  });

  dockButton.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    setBookmarkPanelMinimized(false);
  });

  makeDraggable(panel, header);

  panel.__sevennSetVisible = (visible) => {
    panel.style.display = visible ? 'block' : 'none';
    if (visible) {
      if (bookmarkStoredGeometry) {
        applyPanelGeometry(bookmarkStoredGeometry);
      }
      setBookmarkPanelMinimized(false);
      requestAnimationFrame(() => placeFloatingDialog(panel));
    } else {
      setDockVisible('bookmark', false);
    }
  };
  panel.__sevennSetMinimized = setBookmarkPanelMinimized;
  panel.__sevennGetMinimized = () => bookmarkMinimized;
  panel.__sevennDockButton = dockButton;
  applyBookmarkPanelState();
  panel.__sevennSetVisible(false);

  // --- Load bookmarks ---
 async function loadBookmarksInternal() {
  try {

    const map = getMap();
    if (!map) return;

    const countryId = getSelectedCountryCode();

    if (!countryId) return;

    const res = await fetch(`${API_URL}bookmarks?countryId=${countryId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch bookmarks: ${res.status}`);
    }

    const bookmarks = await res.json();
    bookmarkList.innerHTML = '';

    bookmarks.forEach(b => {
      const li = document.createElement('li');
      li.className = 'list-group-item d-flex justify-content-between align-items-center';

      const nameSpan = document.createElement('span');
      nameSpan.style.cursor = 'pointer';
      nameSpan.textContent = b.name;
      nameSpan.addEventListener('click', () => {
        const map = getMap();
        if (!map) return;

        const center = olProj.transform(
          [b.centerX, b.centerY],
          'EPSG:4326',
          'EPSG:3857'
        );

        map.getView().animate({
          center,
          zoom: b.zoom
        });
      });

      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'btn btn-sm btn-outline-danger';
      deleteBtn.innerHTML = '<i class="bi bi-trash"></i>';
      deleteBtn.title = 'Delete bookmark';
      deleteBtn.addEventListener('click', async () => {
        const confirmed = confirm(
          `Are you sure you want to delete bookmark "${b.name}"?`
        );
        if (!confirmed) return;

        try {
          const response = await fetch(
            `${API_URL}bookmarks/${b.id}`,
            {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (!response.ok) {
            throw new Error('Failed to delete bookmark');
          }

          loadBookmarksInternal();
        } catch (err) {
          console.error(err);
          alert('Error deleting bookmark');
        }
      });

      const flexContainer = document.createElement('div');
      flexContainer.className = 'd-flex justify-content-between align-items-center w-100';
      flexContainer.appendChild(nameSpan);
      flexContainer.appendChild(deleteBtn);

      li.appendChild(flexContainer);
      bookmarkList.appendChild(li);
    });

    //loadBookmarks();

  } catch (err) {
    console.error('Failed to load bookmarks', err);
  }
}


  reloadBookmarks = loadBookmarksInternal;

  // --- Form submission ---
  form.addEventListener('submit', async (e) => {
    e.preventDefault();

      const map = getMap();
  if (!map) {
    console.warn('Map not ready yet, cannot save bookmark');
    return;
  }

    const name = input.value.trim();
    if (!name) {
      messageBox.textContent = 'Please enter a bookmark name.';
      messageBox.style.color = 'red';
      messageBox.style.display = 'block';
      return;
    }

    // Check for duplicates in the current list
    const existingNames = Array.from(bookmarkList.querySelectorAll('span'))
      .map(el => el.textContent.toLowerCase());

    if (existingNames.includes(name.toLowerCase())) {
      messageBox.textContent = `A bookmark named "${name}" already exists.`;
      messageBox.style.color = 'red';
      messageBox.style.display = 'block';
      return;
    }

    const view = map.getView();
    const center = view.getCenter();
    const zoom = view.getZoom();
    const [lon, lat] = olProj.transform(center, 'EPSG:3857', 'EPSG:4326');

    const bookmark = {
      name: name,
      centerX: lon,
      centerY: lat,
      zoom: zoom,
      languageId: getSelectedLanguageCode(),
      countryId: getSelectedCountryCode()
    };

    try {
      const response = await fetch(`${API_URL}bookmarks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',  'Authorization': `Bearer ${token}` },
        body: JSON.stringify(bookmark)
      });

      if (!response.ok) throw new Error('Failed to save bookmark');

      const saved = await response.json();
      messageBox.textContent = `Bookmark "${name}" saved!`;
      messageBox.style.color = 'green';
      messageBox.style.display = 'block';
      form.reset();
      loadBookmarksInternal();
    } catch (err) {
      messageBox.textContent = 'Error saving bookmark.';
      messageBox.style.color = 'red';
      messageBox.style.display = 'block';
      console.error(err);
    }
  });

  return panel;
}
